import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Container } from '../ui/Container';
import '../../styles/hero-title.css';
import { Paperclip, Mic } from 'lucide-react';
import { ProfileCompletionModal } from './ProfileCompletionModal';
import { useUser } from '../../hooks/useUser';
import { useTranslation } from 'react-i18next';
import { AUTH0_CALLBACK_URL, AUTH0_CLIENT_ID, AUTH0_DOMAIN } from '../../config/auth0';
import { startAuth0PKCE } from '../../utils/auth0pkce';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const demoInputs = [
  "Vente de marchandises à crédit pour 500 000 FCFA",
  "Achat de matières premières pour 250 000 FCFA",
  "Paiement de salaires pour 180 000 FCFA",
  "Dotation aux amortissements du matériel informatique",
  "Encaissement d'une créance client de 300 000 FCFA"
];

const demoResponses = [
  {
    match: /vente.*crédit|marchandises.*crédit/i,
    response: "Débit 411 Clients - 590 000 FCFA (TTC), Crédit 701 Ventes de marchandises - 500 000 FCFA, Crédit 4432 État TVA facturée - 90 000 FCFA (Plan SYSCOHADA)"
  },
  {
    match: /achat.*matières|matières premières/i,
    response: "Débit 601 Achats de matières premières - 250 000 FCFA, Débit 44562 État TVA récupérable - 45 000 FCFA, Crédit 401 Fournisseurs - 295 000 FCFA (SYSCOHADA)"
  },
  {
    match: /salaire|paiement.*salaire/i,
    response: "Débit 641 Rémunérations du personnel - 180 000 FCFA, Crédit 421 Personnel rémunérations dues - 144 000 FCFA, Crédit 431 Sécurité sociale - 36 000 FCFA (SYSCOHADA)"
  },
  {
    match: /amortissement|dotation/i,
    response: "Débit 681 Dotations aux amortissements - 50 000 FCFA, Crédit 2845 Amortissements matériel informatique - 50 000 FCFA (Méthode linéaire SYSCOHADA)"
  },
  {
    match: /encaissement.*créance|encaissement.*client/i,
    response: "Débit 521 Banques - 300 000 FCFA, Crédit 411 Clients - 300 000 FCFA (Règlement créance SYSCOHADA)"
  },
  {
    match: /.*/,
    response: "Je suis Adha, votre IA comptable conforme SYSCOHADA/IFRS. Décrivez une opération pour obtenir l'écriture comptable appropriée!"
  }
];

// Helper to parse response into table rows if possible
function parseToTableRows(_input: string, response: string) {
  // Simple heuristics for demo: look for 'Débit' and 'Crédit' and split
  const rows = [];
  const debitMatch = response.match(/Débit ([\d\w ]+),?/i);
  const creditMatch = response.match(/Crédit ([\d\w ]+),?/i);
  if (debitMatch || creditMatch) {
    rows.push({ type: 'Débit', compte: debitMatch ? debitMatch[1] : '', montant: '' });
    rows.push({ type: 'Crédit', compte: creditMatch ? creditMatch[1] : '', montant: '' });
    return rows;
  }
  return null;
}



export function Hero() {
  const { t, i18n } = useTranslation();
  const [autoInput, setAutoInput] = useState("");
  const [demoIndex, setDemoIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [messages, setMessages] = useState<{input: string, response: string}[]>([]);
  const typingTimeout = useRef<NodeJS.Timeout | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Get user auth state and profile completion status
  const { user, isAuthenticated, isLoading } = useUser();

  const handleGetStarted = () => {
    // Revenir sur la page courante après Auth0
    sessionStorage.setItem('auth0_redirect_after_login', window.location.pathname + window.location.search);
    startAuth0PKCE('login', AUTH0_DOMAIN, AUTH0_CLIENT_ID, AUTH0_CALLBACK_URL);
  };
  
  // Traductions des exemples de démo selon la langue
  const demoInputs = i18n.language === 'fr' ? [
    "Vente de marchandises à crédit pour 500 000 FCFA",
    "Achat de matières premières pour 250 000 FCFA",
    "Paiement de salaires pour 180 000 FCFA",
    "Dotation aux amortissements du matériel informatique",
    "Encaissement d'une créance client de 300 000 FCFA"
  ] : [
    "Credit sale of goods for 500,000 FCFA",
    "Purchase of raw materials for 250,000 FCFA",
    "Payment of salaries for 180,000 FCFA",
    "Depreciation of computer equipment",
    "Collection of customer receivable of 300,000 FCFA"
  ];
  
  const demoResponses = i18n.language === 'fr' ? [
    {
      match: /vente.*crédit|marchandises.*crédit|credit sale|goods/i,
      response: "Débit 411 Clients - 590 000 FCFA (TTC), Crédit 701 Ventes de marchandises - 500 000 FCFA, Crédit 4432 État TVA facturée - 90 000 FCFA (Plan SYSCOHADA)"
    },
    {
      match: /achat.*matières|matières premières|purchase.*raw materials/i,
      response: "Débit 601 Achats de matières premières - 250 000 FCFA, Débit 44562 État TVA récupérable - 45 000 FCFA, Crédit 401 Fournisseurs - 295 000 FCFA (SYSCOHADA)"
    },
    {
      match: /salaire|paiement.*salaire|payment.*salaries/i,
      response: "Débit 641 Rémunérations du personnel - 180 000 FCFA, Crédit 421 Personnel rémunérations dues - 144 000 FCFA, Crédit 431 Sécurité sociale - 36 000 FCFA (SYSCOHADA)"
    },
    {
      match: /amortissement|dotation|depreciation/i,
      response: "Débit 681 Dotations aux amortissements - 50 000 FCFA, Crédit 2845 Amortissements matériel informatique - 50 000 FCFA (Méthode linéaire SYSCOHADA)"
    },
    {
      match: /encaissement.*créance|encaissement.*client|collection.*receivable/i,
      response: "Débit 521 Banques - 300 000 FCFA, Crédit 411 Clients - 300 000 FCFA (Règlement créance SYSCOHADA)"
    },
    {
      match: /.*/,
      response: t('hero.adhaGreeting')
    }
  ] : [
    {
      match: /vente.*crédit|marchandises.*crédit|credit sale|goods/i,
      response: "Debit 411 Customers - 590,000 FCFA (Incl. VAT), Credit 701 Sales of goods - 500,000 FCFA, Credit 4432 VAT charged - 90,000 FCFA (SYSCOHADA Plan)"
    },
    {
      match: /achat.*matières|matières premières|purchase.*raw materials/i,
      response: "Debit 601 Purchases of raw materials - 250,000 FCFA, Debit 44562 Recoverable VAT - 45,000 FCFA, Credit 401 Suppliers - 295,000 FCFA (SYSCOHADA)"
    },
    {
      match: /salaire|paiement.*salaire|payment.*salaries/i,
      response: "Debit 641 Staff remuneration - 180,000 FCFA, Credit 421 Staff remuneration due - 144,000 FCFA, Credit 431 Social security - 36,000 FCFA (SYSCOHADA)"
    },
    {
      match: /amortissement|dotation|depreciation/i,
      response: "Debit 681 Depreciation provisions - 50,000 FCFA, Credit 2845 Computer equipment depreciation - 50,000 FCFA (Straight-line method SYSCOHADA)"
    },
    {
      match: /encaissement.*créance|encaissement.*client|collection.*receivable/i,
      response: "Debit 521 Banks - 300,000 FCFA, Credit 411 Customers - 300,000 FCFA (Receivable settlement SYSCOHADA)"
    },
    {
      match: /.*/,
      response: t('hero.adhaGreeting')
    }
  ];

  // Typing and auto-send effect
  useEffect(() => {
    if (charIndex < demoInputs[demoIndex].length) {
      typingTimeout.current = setTimeout(() => {
        setAutoInput(demoInputs[demoIndex].slice(0, charIndex + 1));
        setCharIndex(charIndex + 1);
      }, 60);
    } else {
      // Simulate send after typing
      typingTimeout.current = setTimeout(() => {
        const input = demoInputs[demoIndex];
        const found = demoResponses.find(r => r.match.test(input));
        setMessages(msgs => [...msgs, { input, response: found ? found.response : demoResponses[demoResponses.length - 1].response }]);
        setCharIndex(0);
        setDemoIndex((demoIndex + 1) % demoInputs.length);
        setAutoInput("");
      }, 2200); // slower frequency
    }
    return () => { if (typingTimeout.current) clearTimeout(typingTimeout.current); };
  }, [charIndex, demoIndex, demoInputs, demoResponses]);

  return (
    <div className="relative overflow-hidden min-h-screen" style={{ background: 'linear-gradient(180deg, #f3f4f6 0%, #e5e7eb 100%)' }}>
      {/* Remove top SVG curve for a flat top */}
      {/* <svg className="absolute top-0 left-0 w-full h-32 z-0" ... /> */}
      <svg className="absolute top-0 left-0 w-full h-24 z-0" viewBox="0 0 1440 96" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
        <path d="M0,48 C480,96 960,0 1440,48 L1440,0 L0,0 Z" fill="#f3f4f6" />
      </svg>
      <Container className="relative z-10 py-20 lg:py-32">
        <div className="flex flex-col items-center justify-center text-center">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeIn}
          >
            <div className="min-h-[160px] mt-16 flex justify-center relative" style={{ minHeight: 160 }}>
              <h1
                className="hero-title-special text-4xl font-bold text-gray-900 sm:text-6xl mb-0 relative z-20"
                style={{
                  fontFamily: 'Permanent Marker, Arial, sans-serif',
                  fontWeight: 400,
                  letterSpacing: '0.01em',
                  lineHeight: 1.1,
                  textTransform: 'none'
                }}
              >
                {i18n.language === 'fr' ? 'la plateforme pour gérer et financer votre PME...' : 'the platform to manage and finance your SME...'}
              </h1>
            </div>
            <div className="mt-2 text-lg text-gray-700 font-medium max-w-2xl mx-auto">
              {i18n.language === 'fr' 
                ? "L'intelligence artificielle est une opportunité unique pour accélérer la croissance et assurer la durabilité des PME en RDC."
                : "Artificial intelligence is a unique opportunity to accelerate growth and ensure the sustainability of SMEs in DRC."}
            </div>
            <div className="mt-4 flex justify-center">
              <span
                className="block w-24 h-1 rounded-full bg-primary opacity-80"
                style={{
                  display: 'block',
                  margin: '0 auto'
                }}
              />
            </div>
            <div className="mt-8 flex justify-center relative" style={{ minHeight: 120 }}>
              <div className="w-full max-w-xs relative z-20">
                <div className="relative flex items-center bg-white border rounded-full shadow px-2 py-1 animate-pulse transition-all duration-300 min-h-[38px]">
                  <button type="button" className="p-2 text-gray-400 hover:text-primary transition-colors" tabIndex={-1} aria-label="Micro non-fonctionnel">
                    <Mic className="w-5 h-5" />
                  </button>
                  <textarea
                    className="flex-1 border-0 bg-transparent px-2 py-1 text-sm focus:outline-none focus:ring-0 placeholder-gray-400 resize-none overflow-hidden min-h-[20px] max-h-32"
                    placeholder={t('hero.demoInputPlaceholder')}
                    value={autoInput}
                    readOnly
                    rows={1}
                    style={{ minWidth: 0, height: `${Math.max(20, autoInput.split('\n').length * 20)}px` }}
                  />
                  <button type="button" className="p-2 text-gray-400 hover:text-primary transition-colors" tabIndex={-1} aria-label="Pièce jointe non-fonctionnelle">
                    <Paperclip className="w-5 h-5" />
                  </button>
                </div>
                {/* Résultat IA juste en dessous */}
                <div className="mt-2 space-y-2 text-center text-xs">
                  {messages.length > 0 && (
                    <div className="text-gray-500 inline-block">{messages[messages.length-1].input}</div>
                  )}
                  {messages.length > 0 && (() => {
                    const last = messages[messages.length-1];
                    const tableRows = parseToTableRows(last.input, last.response);
                    if (tableRows) {
                      return (
                        <div className="overflow-x-auto flex justify-center">
                          <table className="min-w-[180px] text-xs border border-gray-200 bg-white rounded-md mx-auto">
                            <thead>
                              <tr className="bg-gray-50">
                                <th className="px-2 py-1 border-b text-left">Type</th>
                                <th className="px-2 py-1 border-b text-left">Compte</th>
                                <th className="px-2 py-1 border-b text-left">Montant</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tableRows.map((row, i) => (
                                <tr key={i}>
                                  <td className="px-2 py-1 border-b">{row.type}</td>
                                  <td className="px-2 py-1 border-b">{row.compte}</td>
                                  <td className="px-2 py-1 border-b text-gray-300 italic">{row.montant || '...'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    }
                    return (
                      <div className="bg-gray-100 rounded-xl px-3 py-2 text-gray-800 border inline-block max-w-full mx-auto">
                        {last.response}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
            
            {/* CTA contextuel (non connecté -> Auth, connecté sans profil -> complétion) */}
            {!isLoading && (
              (!isAuthenticated || (user && !user.companyId && !user.financialInstitutionId))
            ) && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-8 flex flex-col items-center gap-3"
              >
                <button
                  onClick={() => {
                    if (!isAuthenticated) {
                      handleGetStarted();
                      return;
                    }
                    setIsModalOpen(true);
                  }}
                  className="relative px-8 py-3 bg-primary text-white font-semibold rounded-full hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-2"
                >
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                  </span>
                  {isAuthenticated ? t('hero.completeCompanyProfile') : t('common.getStarted')}
                </button>
                
                {/* Info alert */}
                <div className="flex items-start gap-2 max-w-md text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-xs">
                    <span className="font-semibold text-blue-900">{t('hero.limitedAccessLabel')}</span>{' '}
                    {isAuthenticated ? t('hero.limitedAccessMessage') : t('hero.startRequiresAuthMessage')}
                  </p>
                </div>
              </motion.div>
            )}
            
            {/* Tags supprimés pour une présentation plus claire */}
          </motion.div>
        </div>
      </Container>
      
      {/* Profile Completion Modal */}
      <ProfileCompletionModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
}